if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    connectedCallback() {
      this.grid = Array.from(document.querySelectorAll('#product-grid')).find(grid => grid.dataset.id === this.dataset.section);
      this.navigation = this.querySelector('.pagination-links');
      this.status = this.querySelector('[data-pagination-status]');
      if (!this.grid || !this.navigation) return;
      this.loadedUrls = new Set([this.normalizeUrl(window.location.href)]);
      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
      this.onClick = event => {
        const link = event.target.closest('[data-pagination-next]');
        if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || this.failed) return;
        event.preventDefault();
        this.loadProducts(true);
      };
      this.addEventListener('click', this.onClick);
      this.normalizeLinks();
      if (this.classList.contains('pagination-type--infinite') && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting && entry.intersectionRatio === 1)) this.loadProducts(false);
        }, { threshold: [0, 1] });
        this.observeNext();
      }
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      if (this.observer) this.observer.disconnect();
      if (this.controller) this.controller.abort();
      clearTimeout(this.timeout);
      this.removeAttribute('loading');
      this.removeAttribute('aria-busy');
    }
    normalizeUrl(href) {
      const url = new URL(href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) throw new Error('Invalid pagination destination');
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      url.hash = '';
      return url.pathname + url.search;
    }
    normalizeLinks() {
      this.navigation.querySelectorAll('a[href]').forEach(link => {
        link.setAttribute('href', this.normalizeUrl(link.getAttribute('href')));
      });
    }
    observeNext() {
      if (this.observer && this.querySelector('[data-pagination-next]') && !this.failed) this.observer.observe(this);
    }
    async loadProducts(fromClick) {
      const link = this.querySelector('[data-pagination-next]');
      if (!link || this.hasAttribute('loading') || this.failed || !this.isConnected) return;
      if (this.observer) this.observer.unobserve(this);
      this.setAttribute('loading', 'true');
      this.setAttribute('aria-busy', 'true');
      const controller = new AbortController();
      this.controller = controller;
      const timeout = setTimeout(() => controller.abort(), 15000);
      this.timeout = timeout;
      try {
        const url = this.normalizeUrl(link.getAttribute('href'));
        if (this.loadedUrls.has(url)) throw new Error('Repeated pagination destination');
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Pagination request failed');
        if (response.url && this.normalizeUrl(response.url) !== url) throw new Error('Unexpected pagination redirect');
        const html = await response.text();
        if (!this.isConnected || this.controller !== controller || controller.signal.aborted) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const incomingGrid = parsed.getElementById('product-grid');
        const incomingPagination = Array.from(parsed.querySelectorAll('pagination-theme')).find(element => element.dataset.section === this.dataset.section);
        const incomingNavigation = incomingPagination && incomingPagination.querySelector('.pagination-links');
        if (!incomingGrid || incomingGrid.dataset.id !== this.dataset.section || !incomingNavigation) throw new Error('Missing pagination content');
        const products = Array.from(incomingGrid.children).filter(element => element.classList.contains('column'));
        if (!products.length) throw new Error('Empty pagination content');
        const next = incomingNavigation.querySelector('[data-pagination-next]');
        if (next) {
          const nextUrl = this.normalizeUrl(next.getAttribute('href'));
          if (nextUrl === url || this.loadedUrls.has(nextUrl)) throw new Error('Repeated next page');
        }
        incomingNavigation.querySelectorAll('a[href]').forEach(anchor => {
          anchor.setAttribute('href', this.normalizeUrl(anchor.getAttribute('href')));
        });
        const restoreFocus = fromClick && document.activeElement === link;
        this.grid.append(...products);
        this.loadedUrls.add(url);
        this.navigation.replaceChildren(...incomingNavigation.childNodes);
        const incomingStatus = incomingPagination.querySelector('[data-pagination-status]');
        if (this.status && incomingStatus) this.status.textContent = incomingStatus.textContent;
        if (restoreFocus) {
          const focusTarget = products[0].querySelector('a[href], button');
          if (focusTarget) focusTarget.focus({ preventScroll: true });
        }
        if (this.animations_enabled) {
          gsap.fromTo(products, { opacity: 0, y: 30 }, { duration: 0.5, y: 0, opacity: 1, stagger: 0.05, clearProps: 'opacity,transform' });
        }
        if (typeof dispatchCustomEvent === 'function') dispatchCustomEvent('pagination:page-changed', { url });
      } catch (error) {
        if (this.isConnected && this.controller === controller) {
          this.failed = true;
          if (this.status) {
            this.status.textContent = this.dataset.loadError;
            this.status.classList.remove('visually-hidden');
          }
        }
      } finally {
        clearTimeout(timeout);
        if (this.controller === controller) {
          this.controller = null;
          this.removeAttribute('loading');
          this.removeAttribute('aria-busy');
          this.observeNext();
        }
      }
    }
  }
  customElements.define('pagination-theme', Pagination);
}
